import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import PageMeta from '../../components/common/PageMeta';
import SecondaryButton from '../../components/common/SecondaryButton';
import FaqAccordion from '../../components/tools/FaqAccordion';
import BillingToggle from '../../components/pricing/BillingToggle';
import PlanCard from '../../components/pricing/PlanCard';
import PlanComparison from '../../components/pricing/PlanComparison';
import { renderRich } from '../../components/pricing/richText';
import { PRIMARY_COLORS, LIGHT_GRAY, WHITE } from '../../common/colors';
import { ANNUAL_MONTHS_FREE, LIMIT_ROWS, LIMIT_PLAN_KEYS } from '../../common/pricing';
import useSignedIn from '../../common/useSignedIn';
import { PLAN_KEYS, HIGHLIGHTED_PLAN, COMPARISON_ROWS, planPrice, planCta, DEPLOYMENT_CTA, SERVICES_CTA } from './pricingConfig';
import useProCheckout from './useProCheckout';

// /pricing (OpenConceptLab/ocl_online#126). Product-led rather than
// apologetic: the free core is stated first, then the paid tools as an
// addition. The hero's main button and the Pro card's lead to Pro's checkout
// (ocl_online#325); every other CTA is a live destination too (the
// early-access form, a mailto or the app's own signup).
const BeyondCard = ({ heading, text, items, cta, ctaHref, primary }) => (
  <Box sx={{ border: '1px solid', borderColor: primary ? 'primary.main' : LIGHT_GRAY, borderRadius: '12px', p: 4, display: 'flex', flexDirection: 'column' }}>
    <Typography variant="h5" component="h3" sx={{ color: 'surface.dark', mb: 1.5 }}>
      {heading}
    </Typography>
    <Typography variant="body1" sx={{ color: 'surface.contrastText', mb: 2.5 }}>
      {text}
    </Typography>
    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, mb: 3, flexGrow: 1 }}>
      {items.map(item => (
        <Box key={item} component="li" sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.25 }}>
          <CheckIcon sx={{ fontSize: 18, color: 'primary.main', mt: '2px', flexShrink: 0 }} />
          <Typography variant="body2" sx={{ color: 'surface.contrastText' }}>{item}</Typography>
        </Box>
      ))}
    </Box>
    <Box>
      {primary
        ? <Button href={ctaHref} variant="contained" disableElevation>{cta}</Button>
        : <SecondaryButton href={ctaHref}>{cta}</SecondaryButton>}
    </Box>
  </Box>
);

const Pricing = () => {
  const { t, i18n } = useTranslation();
  const [period, setPeriod] = React.useState('monthly');
  // Read after mount (docs/SEO-AUDIT.md bug #8): the Community card changes
  // for signed-in visitors.
  const signedIn = useSignedIn();
  const pro = useProCheckout();
  const proCta = planCta('pro', t, { pro });
  const faqs = t('pricing.faq', { returnObjects: true });

  return (
    <Box>
      <PageMeta title={t('pricing.hero.title')} description={t('pricing.meta_description')} />

      <Box sx={{ backgroundColor: 'primary.main' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 9 }, textAlign: 'center' }}>
          <Typography sx={{ color: PRIMARY_COLORS['90'], fontWeight: 600, mb: 1 }}>
            {t('pricing.hero.eyebrow')}
          </Typography>
          <Typography variant="h1" sx={{ color: WHITE, fontSize: { xs: '2.5rem', md: '3.25rem' }, maxWidth: 900, mx: 'auto', mb: 2 }}>
            {t('pricing.hero.title')}
          </Typography>
          <Typography sx={{ color: PRIMARY_COLORS['90'], fontSize: '1.125rem', maxWidth: 720, mx: 'auto', mb: 4 }}>
            {t('pricing.hero.text')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
            <Button href={proCta.href} onClick={proCta.onClick} sx={{ backgroundColor: WHITE, color: 'primary.main', '&:hover': { backgroundColor: PRIMARY_COLORS['90'] } }}>
              {proCta.label}
            </Button>
            <Button href="#plans" sx={{ border: `1px solid ${WHITE}`, color: WHITE, '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}>
              {t('pricing.hero.secondary_cta')}
            </Button>
          </Box>
          {proCta.notes.map(note => (
            <Typography key={note} sx={{ color: PRIMARY_COLORS['90'], fontSize: '0.875rem', mt: 2 }}>
              {renderRich(note)}
            </Typography>
          ))}
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 }, textAlign: 'center' }}>
        <Typography variant="h3" component="h2" sx={{ color: 'surface.dark', fontSize: { xs: '1.75rem', md: '2.25rem' }, mb: 2 }}>
          {t('pricing.open.heading')}
        </Typography>
        {/* renderRich keeps links `color: inherit` for card bullets, where a
            coloured link inside a dense list is noise. In running prose the
            site's convention is a brand-coloured link, so recolour here. */}
        <Typography sx={{ color: 'surface.contrastText', fontSize: '1.125rem', '& a': { color: 'primary.main' } }}>
          {renderRich(t('pricing.open.text'))}
        </Typography>
        <Typography sx={{ mt: 3 }}>
          <Box component="a" href="#plans" sx={{ color: 'primary.main', fontWeight: 600, textDecoration: 'none' }}>
            {t('pricing.open.link')}
          </Box>
        </Typography>
      </Container>

      <Box id="plans" sx={{ backgroundColor: PRIMARY_COLORS['95'], scrollMarginTop: '80px' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography variant="h3" component="h2" sx={{ color: 'surface.dark', fontSize: { xs: '1.75rem', md: '2.25rem' }, mb: 1.5 }}>
              {t('pricing.plansHeading')}
            </Typography>
            <Typography sx={{ color: 'surface.contrastText' }}>{t('pricing.plansText')}</Typography>
            {/* The prices below are first-year prices (ocl_online#226). Said
                under the heading, before the cards, so it's read before the
                prices are: below the cards it went unseen. */}
            <Typography sx={{ color: 'surface.contrastText', maxWidth: 720, mx: 'auto', mt: 3 }}>
              {renderRich(t('pricing.introNote'))}
            </Typography>
            {/* The subscription dates (ocl_online#299), next to the prices
                they apply to. The FAQ's "When does it start?" has the detail. */}
            <Typography sx={{ color: 'surface.contrastText', maxWidth: 720, mx: 'auto', mt: 2 }}>
              {renderRich(t('pricing.datesNote'))}
            </Typography>
          </Box>
          <BillingToggle
            monthlyLabel={t('pricing.toggle.monthly')}
            annualLabel={t('pricing.toggle.annual', { months: ANNUAL_MONTHS_FREE })}
            subLabel={t('pricing.toggle.annualSubLabel')}
            value={period}
            onChange={setPeriod}
          />
          {/* Two across until lg (ocl_online#261). Some card content can't
              break (the annual prices, for one), and four across from md
              left each card under 150px of room, so the last one ran off the
              page. minmax(0, 1fr), not 1fr: a bare 1fr column grows to fit
              whatever can't break, and pushes the next one along. */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 3, alignItems: 'stretch' }}>
            {PLAN_KEYS.map(key => {
              const price = planPrice(key, period, t);
              return (
                <PlanCard
                  key={key}
                  name={t(`pricing.plans.${key}.name`)}
                  badge={key === HIGHLIGHTED_PLAN ? t('pricing.plans.team.badge') : undefined}
                  highlighted={key === HIGHLIGHTED_PLAN}
                  priceMain={price.main}
                  priceSub={price.sub}
                  tagline={t(`pricing.plans.${key}.tagline`)}
                  featuresIntro={t(`pricing.plans.${key}.featuresIntro`)}
                  features={t(`pricing.plans.${key}.features`, { returnObjects: true })}
                  cta={planCta(key, t, { signedIn, pro })}
                />
              );
            })}
          </Box>
          <PlanComparison rows={COMPARISON_ROWS} planKeys={PLAN_KEYS} limitRows={LIMIT_ROWS} limitPlanKeys={LIMIT_PLAN_KEYS} t={t} lng={i18n.resolvedLanguage} />
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h3" component="h2" sx={{ color: 'surface.dark', fontSize: { xs: '1.75rem', md: '2.25rem' }, mb: 1.5 }}>
            {t('pricing.beyond.heading')}
          </Typography>
          <Typography sx={{ color: 'surface.contrastText' }}>{t('pricing.beyond.text')}</Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
          <BeyondCard
            primary
            heading={t('pricing.beyond.production.heading')}
            text={t('pricing.beyond.production.text')}
            items={t('pricing.beyond.production.items', { returnObjects: true })}
            cta={t('pricing.beyond.production.cta')}
            ctaHref={DEPLOYMENT_CTA}
          />
          <BeyondCard
            heading={t('pricing.beyond.services.heading')}
            text={t('pricing.beyond.services.text')}
            items={t('pricing.beyond.services.items', { returnObjects: true })}
            cta={t('pricing.beyond.services.cta')}
            ctaHref={SERVICES_CTA}
          />
        </Box>
      </Container>

      <Box sx={{ backgroundColor: PRIMARY_COLORS['95'] }}>
        <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 } }}>
          <Typography variant="h3" component="h2" sx={{ color: 'surface.dark', fontSize: { xs: '1.75rem', md: '2.25rem' }, textAlign: 'center', mb: 4 }}>
            {t('pricing.faqHeading')}
          </Typography>
          <FaqAccordion faqs={faqs} withBorder />
        </Container>
      </Box>

    </Box>
  );
};

export default Pricing;
